/**
 * The playing-card deck (EN-10656): 52 faces and one back, designer-owned,
 * exported from Figma. The code never typesets a rank or draws a suit — every
 * card is one artwork that is sized by width and scaled as a unit, so a card
 * reads the same at 24px in the winner banner as at 52px on the felt.
 *
 * HOW A FACE SHIPS (review #3836). The 52 exports are compositions of 13 rank
 * glyphs and 4 suit glyph pairs on one card blank, each path byte-identical
 * wherever it recurs, coloured by one fill per suit. deckArt.generated.ts holds
 * that unique geometry (built from the exports by `yarn deck:build`), and this
 * module composes each face back — the same primitives, the same paint order,
 * the same fill — as an inline SVG data URI. Pixel-identical to the export, no
 * fetch, nothing to warm. The exports stay in src/assets/cards/deck/ as the
 * designer's source of truth; nothing imports them.
 *
 * THE BACK is the one genuinely unique drawing (64 clipped paths in the
 * export) and the most-instanced artwork on the felt, so it ships as a small
 * lossless WebP raster of the export (363 × 600, sharp at 3× for every
 * surface that shows a back) rather than as vector the GPU re-rasterises at
 * every size it appears.
 *
 * `cardFaceSrc` takes the same strings the engine sends (`Ah`, `Kd`, `10s`),
 * plus the lobby's `T` for ten and the dock's unicode suit glyphs.
 */
import cardBack from 'assets/cards/deck/back.webp';

import {
  DECK_FACE_RECT,
  DECK_FACE_SVG_OPEN,
  DECK_RANK_PATHS,
  DECK_SUIT_FILL,
  DECK_SUIT_PATHS,
  DeckRank,
  DeckSuit
} from './deckArt.generated';

/** Width ÷ height of every card in the deck (Figma master: 605 × 1000). */
export const CARD_ASPECT_RATIO = 605 / 1000;

/**
 * Corner radius ÷ width (Figma master: rx 64 on a 605-wide card). Round a
 * card's box with this so shadows, glows and dashed outlines hug the
 * artwork's corners instead of poking out past them.
 */
export const CARD_CORNER_RADIUS_RATIO = 64 / 605;

export const cardHeightForWidth = (width: number): number =>
  Math.round((width / CARD_ASPECT_RATIO) * 100) / 100;

export const cardCornerRadiusForWidth = (width: number): number =>
  Math.round(width * CARD_CORNER_RADIUS_RATIO * 100) / 100;

export const CARD_BACK_SRC: string = cardBack;
export const CARD_BACK_ALT = 'Face-down card';

const RANKS: readonly DeckRank[] = [
  '10',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'A',
  'J',
  'K',
  'Q'
];
const SUITS: readonly DeckSuit[] = ['c', 'd', 'h', 's'];

/**
 * One face as SVG markup, element for element the export: its own <svg> root
 * (intrinsic 605 × 1000), the blank, the rank glyph(s), then the suit's index
 * glyph and centre pip, all in the suit's fill, in the export's paint order —
 * so the browser rasterises exactly what the file would have.
 */
export const composeFaceSvg = (rank: DeckRank, suit: DeckSuit): string => {
  const fill = DECK_SUIT_FILL[suit];
  const paths = [...DECK_RANK_PATHS[rank], ...DECK_SUIT_PATHS[suit]]
    .map((d) => `<path d="${d}" fill="${fill}"/>`)
    .join('');
  return `${DECK_FACE_SVG_OPEN}${DECK_FACE_RECT}${paths}</svg>`;
};

const toDataUri = (svg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

// Composed once at module load: 52 strings of ~2.5 KB, keyed by engine notation.
const FACES: Record<string, string> = {};
RANKS.forEach((rank) => {
  SUITS.forEach((suit) => {
    FACES[`${rank}${suit}`] = toDataUri(composeFaceSvg(rank, suit));
  });
});

/** Every face key, engine notation — 52 of them. */
export const CARD_FACE_KEYS: readonly string[] = Object.keys(FACES);

const SUIT_LETTER: Record<string, string> = {
  '♠': 's',
  '♥': 'h',
  '♦': 'd',
  '♣': 'c'
};

const SUIT_NAME: Record<string, string> = {
  c: 'clubs',
  d: 'diamonds',
  h: 'hearts',
  s: 'spades'
};

const normalizeRank = (rank: string): string => {
  const upper = rank.toUpperCase();
  return upper === 'T' ? '10' : upper;
};

const normalizeSuit = (suit: string): string => SUIT_LETTER[suit] ?? suit.toLowerCase();

/**
 * The artwork URL for a face, or undefined when the pair is not a card in the
 * deck — callers render nothing for those, never a back (a back is only ever
 * the concealment of a real face).
 */
export const cardFaceSrc = (rank?: string | null, suit?: string | null): string | undefined => {
  if (!rank || !suit) return undefined;
  return FACES[`${normalizeRank(rank)}${normalizeSuit(suit)}`];
};

/** Screen-reader label for a face: "A of spades", "10 of hearts". */
export const cardAltText = (rank?: string | null, suit?: string | null): string => {
  if (!rank || !suit) return '';
  const s = normalizeSuit(suit);
  return `${normalizeRank(rank)} of ${SUIT_NAME[s] ?? s}`;
};

let preloaded = false;

/**
 * Warm the one fetched artwork — the back. Faces are composed in-bundle and
 * need no fetch. Without this, the first face-down deal after launch could
 * show a blank card for the round trip of an image never fetched. Idempotent;
 * safe to call from any mount. App.tsx schedules it for browser idle time.
 */
export const preloadCardDeck = (): void => {
  if (preloaded || typeof Image === 'undefined') return;
  preloaded = true;
  const img = new Image();
  img.decoding = 'async';
  img.src = cardBack;
};
